/*
 * Excel-Dateien (.xlsx) direkt lesen (0.47) – ohne Zusatzpaket, im Browser und in Node gleich.
 * Eine .xlsx ist ein ZIP mit XML-Dateien; gelesen werden nur Zellwerte (Text, Zahlen, Wahrheitswerte,
 * Datumsangaben über das Zahlenformat). Formeln liefern ihren zuletzt berechneten Wert.
 * Entpacken über DecompressionStream('deflate-raw') (alle aktuellen Browser, Node ≥ 18).
 */

export interface XlsxSheet {
  name: string;
  rows: string[][];
}

export class XlsxError extends Error {}

const LIMIT_ROWS = 20_000;
const LIMIT_COLS = 200;

/* ---------- ZIP ---------- */

interface ZipEntry {
  name: string;
  method: number;
  offset: number;
  size: number;
}

function zipEntries(b: Uint8Array): Map<string, ZipEntry> {
  const v = new DataView(b.buffer, b.byteOffset, b.byteLength);
  // Ende des zentralen Verzeichnisses (höchstens 64 KB Kommentar dahinter)
  let eocd = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 22 - 65_535); i--) {
    if (v.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new XlsxError('Das ist keine Excel-Datei (.xlsx).');
  const count = v.getUint16(eocd + 10, true);
  let p = v.getUint32(eocd + 16, true);
  const out = new Map<string, ZipEntry>();
  const dec = new TextDecoder();
  for (let i = 0; i < count; i++) {
    if (p + 46 > b.length || v.getUint32(p, true) !== 0x02014b50) throw new XlsxError('Die Excel-Datei ist beschädigt.');
    const method = v.getUint16(p + 10, true);
    const size = v.getUint32(p + 20, true);
    const nameLen = v.getUint16(p + 28, true);
    const extra = v.getUint16(p + 30, true);
    const comment = v.getUint16(p + 32, true);
    const local = v.getUint32(p + 42, true);
    const name = dec.decode(b.subarray(p + 46, p + 46 + nameLen));
    if (local + 30 > b.length || v.getUint32(local, true) !== 0x04034b50) throw new XlsxError('Die Excel-Datei ist beschädigt.');
    const offset = local + 30 + v.getUint16(local + 26, true) + v.getUint16(local + 28, true);
    out.set(name, { name, method, offset, size });
    p += 46 + nameLen + extra + comment;
  }
  return out;
}

async function inflate(data: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate-raw');
  const w = ds.writable.getWriter();
  void w.write(new Uint8Array(data)).catch(() => undefined);
  void w.close().catch(() => undefined);
  try {
    return new Uint8Array(await new Response(ds.readable).arrayBuffer());
  } catch {
    throw new XlsxError('Die Excel-Datei ist beschädigt.');
  }
}

async function readEntry(b: Uint8Array, entries: Map<string, ZipEntry>, name: string): Promise<string | null> {
  const e = entries.get(name);
  if (!e) return null;
  const raw = b.subarray(e.offset, e.offset + e.size);
  if (e.method !== 0 && e.method !== 8) throw new XlsxError('Diese Excel-Datei ist auf eine Art gepackt, die KaBa nicht lesen kann.');
  const data = e.method === 8 ? await inflate(raw) : raw;
  if (data.length > 80 * 1024 * 1024) throw new XlsxError('Die Excel-Datei ist zu groß.');
  return new TextDecoder().decode(data);
}

/* ---------- XML-Helfer (bewusst einfach: die Struktur von SpreadsheetML ist fest) ---------- */

const ENT: Record<string, string> = { lt: '<', gt: '>', amp: '&', quot: '"', apos: "'" };
export function xmlText(s: string): string {
  return s
    .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) =>
      e[0] === '#' ? String.fromCodePoint(e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10)) : (ENT[e] ?? m),
    )
    .replace(/_x([0-9A-F]{4})_/g, (_m, h: string) => String.fromCharCode(parseInt(h, 16)));
}
const attr = (tag: string, name: string): string | null => {
  const m = new RegExp(`\\s${name}="([^"]*)"`).exec(tag);
  return m ? xmlText(m[1]!) : null;
};
/** Text aus <t>-Elementen (Formatierungsläufe zusammengesetzt, Lautschrift <rPh> ohne) */
const texts = (xml: string) =>
  [...xml.replace(/<rPh\b[\s\S]*?<\/rPh>/g, '').matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)].map((m) => xmlText(m[1]!)).join('');

/* ---------- Datumsformate ---------- */

const DATE_BUILTIN = new Set([14, 15, 16, 17, 18, 19, 20, 21, 22, 45, 46, 47]);
function isDateFormat(code: string): boolean {
  const c = code
    .replace(/"[^"]*"/g, '')
    .replace(/\[[^\]]*\]/g, '')
    .replace(/\\./g, '');
  return /[dmyhs]/i.test(c) && !/^[#0.,%\s]*$/.test(c);
}
const pad = (n: number) => String(n).padStart(2, '0');
/** Excel-Seriennummer → „2026-10-21“ bzw. „2026-10-21 14:30“ (ohne Zeitzone, wie in der Tabelle gesehen) */
export function excelSerialToText(serial: number, date1904 = false): string {
  const ms = Math.round((serial + (date1904 ? 1462 : 0)) * 86_400_000) + Date.UTC(1899, 11, 30);
  const d = new Date(ms);
  const day = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  return serial % 1 === 0 ? day : `${day} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/* ---------- Lesen ---------- */

const colIndex = (ref: string) => {
  let n = 0;
  for (const ch of ref.replace(/\d+$/, '')) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
};

export async function readXlsx(input: ArrayBuffer | Uint8Array): Promise<XlsxSheet[]> {
  const b = input instanceof Uint8Array ? input : new Uint8Array(input);
  const entries = zipEntries(b);
  const workbook = await readEntry(b, entries, 'xl/workbook.xml');
  if (!workbook) throw new XlsxError('Das ist keine Excel-Arbeitsmappe (.xlsx).');
  const rels = (await readEntry(b, entries, 'xl/_rels/workbook.xml.rels')) ?? '';
  const target = new Map([...rels.matchAll(/<Relationship\b[^>]*>/g)].map((m) => [attr(m[0], 'Id') ?? '', attr(m[0], 'Target') ?? '']));
  const date1904 = /date1904="(1|true)"/.test(workbook);

  const shared = [...((await readEntry(b, entries, 'xl/sharedStrings.xml')) ?? '').matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => texts(m[1]!));

  const styles = (await readEntry(b, entries, 'xl/styles.xml')) ?? '';
  const custom = new Map([...styles.matchAll(/<numFmt\b[^>]*>/g)].map((m) => [Number(attr(m[0], 'numFmtId')), attr(m[0], 'formatCode') ?? '']));
  const xfs = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(styles)?.[1] ?? '';
  const dateStyle = [...xfs.matchAll(/<xf\b[^>]*>/g)].map((m) => {
    const id = Number(attr(m[0], 'numFmtId') ?? 0);
    return DATE_BUILTIN.has(id) || (custom.has(id) && isDateFormat(custom.get(id)!));
  });

  const sheets: XlsxSheet[] = [];
  for (const m of workbook.matchAll(/<sheet\b[^>]*>/g)) {
    const name = attr(m[0], 'name') ?? `Blatt ${sheets.length + 1}`;
    const rid = attr(m[0], 'r:id') ?? '';
    const t = target.get(rid);
    if (!t) continue;
    const path = t.startsWith('/') ? t.slice(1) : `xl/${t.replace(/^\.\//, '')}`;
    const xml = await readEntry(b, entries, path);
    if (!xml) continue;
    const rows: string[][] = [];
    for (const r of xml.matchAll(/<row\b([^>]*)>([\s\S]*?)<\/row>/g)) {
      const rowNo = Number(attr(r[1]!, 'r') ?? rows.length + 1) - 1;
      if (rowNo >= LIMIT_ROWS) break;
      const row: string[] = [];
      for (const c of r[2]!.matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
        const head = c[1]!;
        const body = c[2] ?? '';
        const ref = attr(head, 'r');
        const i = ref ? colIndex(ref) : row.length;
        if (i < 0 || i >= LIMIT_COLS) continue;
        const type = attr(head, 't');
        const raw = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1];
        let value = '';
        if (type === 's') value = shared[Number(raw)] ?? '';
        else if (type === 'inlineStr') value = texts(/<is>([\s\S]*?)<\/is>/.exec(body)?.[1] ?? '');
        else if (type === 'str') value = xmlText(raw ?? '');
        else if (type === 'b') value = raw === '1' ? 'true' : 'false';
        else if (type === 'e') value = '';
        else if (raw !== undefined) {
          const n = Number(raw);
          value = Number.isFinite(n) && dateStyle[Number(attr(head, 's') ?? 0)] ? excelSerialToText(n, date1904) : raw;
        }
        while (row.length < i) row.push('');
        row[i] = value;
      }
      while (rows.length < rowNo) rows.push([]);
      rows[rowNo] = row;
    }
    sheets.push({ name, rows });
  }
  if (!sheets.length) throw new XlsxError('In der Excel-Datei wurde kein Tabellenblatt gefunden.');
  return sheets;
}
